import React from "react";

import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import ProjectAdminForm from "./projectAdminForm/ProjectAdminForm";
import { CREATE_PROJECT } from "../../actions/ActionTypes";
import { notifError } from "../../actions/FlashMessageActions";
import { postApi } from "../../actions/SendActions";
import { TypeEnum } from "../../api/ProjectAPI.types";
import "../../css/admin/smartlist.scss";
import { validateSocialNetworkLinks } from "../../utils/data/Project";
import history from "../common/navigation/AppHistory";

import type { JSX } from "react";

import type { Project, UpdatedProject } from "../../api/ProjectAPI.types";
import type { AppState } from "../../reducers";

function ProjectAdminCreate(): JSX.Element {
  const { t } = useTranslation("editor-project");

  const token = useSelector((state: AppState) => state.loginReducer.token);

  const projectLimitReached = useSelector((state: AppState) => {
    const { orga_config: orgaConfig, projects } = state.fetchReducer;
    return (
      projects?.data.length >= orgaConfig?.data?.organization_options?.sfp_limit
    );
  });

  const dispatch = useDispatch();

  const defaultProject: UpdatedProject = {
    enabled: false,
    type: TypeEnum.Standard,
    name: t("newProject"),
    translations: [],
    translations_indexes: { language: {}, default: { true: [] } },
    storefront_project_logins: [],
    social_network_links: [],
    trackers: [],
    page_links: [],
  } as unknown as UpdatedProject;

  const onCreateError = (error: { details: string }): void => {
    dispatch(
      notifError(t("editor-notif:saveError"), [
        t(`editor-errors:${error.details}`),
      ]),
    );
  };

  const itemIsValid = (project: UpdatedProject): string[] => {
    const errorMessages: string[] = [];

    validateSocialNetworkLinks(project.social_network_links, t, errorMessages);

    if (!project.name || project.name.length <= 0) {
      errorMessages.push(t("editor-errors:noURL"));
    } else if (!/^[A-Za-z0-9]+(?:[-_][A-Za-z0-9]+)*$/.test(project.name)) {
      errorMessages.push(t("editor-errors:invalidURL"));
    }

    project.translations.forEach((translation) => {
      if (!translation.title) {
        errorMessages.push(t("editor-errors:noTitle"));
      }
    });

    return errorMessages;
  };

  const onCreate = (project: UpdatedProject | Project): void => {
    if (projectLimitReached) {
      dispatch(notifError(t("editor-errors:sfp-limit-reached")));
      return;
    }
    // Format model for saving
    const projectTranslationsUpdated = project.translations.map(
      (translation) => {
        if ("uuid" in translation && !translation?.uuid) {
          const { uuid, ...translationWithoutUuid } = translation;
          return translationWithoutUuid;
        }
        return translation;
      },
    );

    const projectUpdated = {
      ...project,
      translations: projectTranslationsUpdated,
    };

    const errorMessages = itemIsValid(projectUpdated);
    if (errorMessages.length === 0) {
      dispatch(
        postApi({
          endpoint: "/api/project/v3/admin/storefront_projects/create",
          type: CREATE_PROJECT,
          item: "project",
          token,
          data: projectUpdated,
          message: t("editor-notif:saveSuccess"),
          error: t("editor-notif:saveError"),
          callback: () => {
            history.push(`/`);
          },
          callbackError: onCreateError,
        }),
      );
    } else {
      dispatch(notifError(t("editor-notif:saveError"), errorMessages));
    }
  };

  return (
    <div className="editor-smartlist-update-container">
      {projectLimitReached ? (
        <h3>{t("editor-errors:sfp-limit-reached")}</h3>
      ) : (
        <ProjectAdminForm
          itemIsValid={itemIsValid}
          onSubmit={onCreate}
          project={defaultProject}
          type="new"
        />
      )}
    </div>
  );
}

export default ProjectAdminCreate;
